import {
	AfterViewChecked,
	ChangeDetectorRef,
	Component,
	OnInit,
} from '@angular/core';
import { BroadcastService, ObjectUtils } from "@cv-invision/core";
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ActivatedRoute } from "@angular/router";
import { IntegrationsUrlParamsModel } from "../../models/integrations-url-params.model";
import { OutgoingEventsHybridService } from "../../../outgoing-events-hybrid/service/outgoing-events-hybrid.service";
import { HybridDataModel } from "../../models/outgoing-events.model";
import {
	outgoingEventsFormConfig,
	tabsConfiguration,
} from './model/outgoing-events.model';
import { GenericFormService } from "@cv-invision/cv.invision-views.ux";
import { MessageService } from "primeng/api";

@Component({
	selector: 'cv-invision-generic-outgoing-events-hybrid-tabs',
	templateUrl: './outgoing-events-hybrid-tabs.component.html',
	styleUrls: ['./outgoing-events-hybrid-tabs.component.scss'],
	providers: [GenericFormService]
})
export class OutgoingEventsHybridTabsComponent implements OnInit, AfterViewChecked {
	public params = new IntegrationsUrlParamsModel();
	public integrationId: string;
	public saving: boolean;
	public isEditing: boolean;
	public selectedTabIndex = 0;
	public hybridData: HybridDataModel;
	public originalFormValue: any;
	public tabsConfiguration = tabsConfiguration;
	public displayForm = false;
	public isDirty = false;
	private outgoingEventsFormConfig: any;

	private atComponentDestroy: Subject<any> = new Subject<any>();

	get validForm(): boolean {
		return this.genericFormService.form?.valid;
	}

	constructor(
		private route: ActivatedRoute,
		private outgoingEventHybridService: OutgoingEventsHybridService,
		private broadcastService: BroadcastService,
		private genericFormService: GenericFormService,
		private cdref: ChangeDetectorRef,
		private messageService: MessageService,
	) {
		this.saving = false;
	}
	ngAfterViewChecked(): void {
		this.cdref.detectChanges();
	}

	ngOnInit(): void {
		this.outgoingEventsFormConfig = ObjectUtils.recursiveDeepCopy(outgoingEventsFormConfig);
		this.genericFormService.formObserver
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data) => {
				if (data) {
					this.displayForm = true;

					this.genericFormService.form
						.valueChanges
						.pipe(takeUntil(this.atComponentDestroy))
						.subscribe((data) => {
							this.isDirty = JSON.stringify(data) !== JSON.stringify(this.originalFormValue);
						});
				}
			});

		this.route.params
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(params => {
				this.params = new IntegrationsUrlParamsModel();
				this.integrationId = this.route.snapshot.paramMap.get('organizationIntegrationId') ? this.route.snapshot.paramMap.get('organizationIntegrationId') : params.integrationId;
				this.loadDataOutgoingEventHybrid(true);
			});
	}

	/**
	 * Loads outgoing event data for the current integration and populates the form arrays.
	 */
	private loadDataOutgoingEventHybrid(initForm: boolean) {
		this.outgoingEventHybridService
			.getIntegrationOutgoingEventsbyOrganizationId(this.integrationId)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.hybridData = new HybridDataModel(result.data);
				if (initForm) {

					const eventNameOptions = Object.entries(result.cmpRelation).map(([key, label]) => ({
						value: key,
						label: label
					}));

					const postIf = result.postValues.map(item => ({
						value: item,
						label: item
					}));
					this.genericFormService.setFormFieldList(ObjectUtils.recursiveDeepCopy(this.outgoingEventsFormConfig));


					this.genericFormService.formFieldList['product']['childFormBuilderConfig']['eventName']['options'] = eventNameOptions;
					this.genericFormService.formFieldList['product']['childFormBuilderConfig']['postIf']['options'] = postIf;

					this.genericFormService.formFieldList['productHybrid']['childFormBuilderConfig']['eventName']['options'] = eventNameOptions;
					this.genericFormService.formFieldList['productHybrid']['childFormBuilderConfig']['postIf']['options'] = postIf;

					this.genericFormService.formFieldList['productNonHybrid']['childFormBuilderConfig']['eventName']['options'] = eventNameOptions;
					this.genericFormService.formFieldList['productNonHybrid']['childFormBuilderConfig']['postIf']['options'] = postIf;

					this.genericFormService.initForm(this.hybridData);
				}

				this.originalFormValue = ObjectUtils.recursiveDeepCopy(this.genericFormService.form.value);
				this.isDirty = false;
			});
	}

	/**
	 * Saves the form data using the service, shows success/error toasts, and reloads data.
	 * @param formData - The data to save.
	 * @param isEditMode - Whether the save is in edit mode.
	 * @param integrationId - The ID of the current integration.
	 */
	onSave(): void {
		this.saving = true;
		const data = this.genericFormService.form?.value;
		this.outgoingEventHybridService
			.saveOutgoingEvents(this.isEditing, this.integrationId, data)
			.subscribe({
				next: (response) => {
					this.saving = false;
					if (response) {
						this.showToast(`The Outgoing Event Was Updated Successfully`, 'success');
						this.loadDataOutgoingEventHybrid(false);
						this.genericFormService.form.markAsPristine();
					} else {
						this.showToast('Failed to save outgoing events.', 'error');
					}
				},
				error: (err) => {
					this.saving = false;
					this.showToast('An unexpected error occurred while saving.', 'error');
					console.error(err);
				}
			});
	}

	/**
	 * Displays a toast notification.
	 * @param message - The message to show.
	 * @param type - The type of the toast (e.g. success or error).
	 */
	private showToast(message: string, type: string): void {
		this.messageService.add({
			severity: type,
			summary: 'Success',
			detail: message
		});
	}
}
